import React, { useState, useEffect } from 'react';
import Modal, { PrimaryButton, SecondaryButton } from './Modal.jsx';
import { enumOptions } from '../../lib/enums.js';

const fieldLabel = { fontSize: 11, textTransform: 'uppercase', color: '#9A9A95', letterSpacing: '0.04em', marginBottom: 6 };
const inputStyle = {
  width: '100%', fontSize: 13, color: '#1A1A1A',
  padding: '7px 9px', border: '1px solid #E0DFD9', borderRadius: 6, background: '#fff', outline: 'none'
};

function Toggle({ options, value, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {options.map(({ value: v, label }) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          style={{
            padding: '8px 10px', fontSize: 12.5, fontWeight: 500,
            border: '1px solid', borderRadius: 6, cursor: 'pointer',
            borderColor: value === v ? '#1A1A1A' : '#E0DFD9',
            background:  value === v ? '#1A1A1A' : '#fff',
            color:       value === v ? '#fff'    : '#3A3A38'
          }}
        >{label}</button>
      ))}
    </div>
  );
}

const FLAG_COLOR = { on_track: '#1F6B41', watch: '#B4791A', at_risk: '#9B1F26' };

export default function LogFeedbackModal({ open, onClose, projectId, entry = null, onSaved }) {
  const isEdit = !!entry;

  const [kind, setKind]             = useState(entry?.kind || 'client');
  const [sentiment, setSentiment]   = useState(entry?.sentiment || 'positive');
  const [title, setTitle]           = useState(entry?.title || '');
  const [body, setBody]             = useState(entry?.body || '');
  const [severity, setSeverity]     = useState(entry?.severity || 'medium');
  const [channel, setChannel]       = useState(entry?.channel || 'email');
  const [status, setStatus]         = useState(entry?.status || 'open');
  const [resolution, setResolution] = useState(entry?.resolution || '');
  const [healthFlag, setHealthFlag] = useState(entry?.health_flag || null);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState(null);

  useEffect(() => {
    if (open) {
      setKind(entry?.kind || 'client');
      setSentiment(entry?.sentiment || 'positive');
      setTitle(entry?.title || '');
      setBody(entry?.body || '');
      setSeverity(entry?.severity || 'medium');
      setChannel(entry?.channel || 'email');
      setStatus(entry?.status || 'open');
      setResolution(entry?.resolution || '');
      setHealthFlag(entry?.health_flag || null);
      setError(null);
    }
  }, [open, entry]);

  async function submit() {
    if (!title.trim()) return;
    setSaving(true);
    setError(null);
    try {
      let payload;
      if (kind === 'internal') {
        payload = { kind: 'internal', title: title.trim(), body: body.trim(), health_flag: healthFlag };
      } else {
        payload = { kind: 'client', sentiment, title: title.trim(), body: body.trim(), channel };
        if (sentiment === 'concern') payload.severity = severity;
      }
      if (isEdit && kind !== 'internal') {
        payload.status = status;
        payload.resolution = resolution.trim();
      }

      const url    = isEdit ? `/feedback_entries/${entry.id}` : `/projects/${projectId}/feedback_entries`;
      const method = isEdit ? 'PATCH' : 'POST';

      const res = await fetch(url, {
        method,
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
          'Accept':       'application/json'
        },
        body: JSON.stringify({ feedback_entry: payload })
      });

      if (res.status === 401) { window.location = '/session/new'; return; }
      if (res.status === 403) {
        setError("You don't have permission — only the author or a project manager can change this entry.");
        return;
      }
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error((errBody.errors && errBody.errors.join(', ')) || `Save failed (${res.status})`);
      }

      onSaved(await res.json());
      onClose();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const isInternal = kind === 'internal';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit entry' : 'Log entry'}
      footer={
        <>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton onClick={submit} disabled={saving || !title.trim()}>
            {saving ? 'Saving…' : (isEdit ? 'Save' : 'Log')}
          </PrimaryButton>
        </>
      }
    >
      {!isEdit && (
        <div className="mb-4">
          <div style={fieldLabel}>Type</div>
          <Toggle options={enumOptions('feedback_kind')} value={kind} onChange={setKind} />
        </div>
      )}

      {!isEdit && !isInternal && (
        <div className="mb-4">
          <div style={fieldLabel}>Sentiment</div>
          <Toggle options={enumOptions('feedback_sentiment')} value={sentiment} onChange={setSentiment} />
        </div>
      )}

      <div className="mb-4">
        <div style={fieldLabel}>{isInternal ? 'Summary' : 'Title'}</div>
        <input style={inputStyle} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="One-line summary" />
      </div>

      <div className="mb-4">
        <div style={fieldLabel}>{isInternal ? 'Note (optional)' : 'Details'}</div>
        <textarea style={{ ...inputStyle, minHeight: 84, resize: 'vertical' }} value={body} onChange={(e) => setBody(e.target.value)} placeholder={isInternal ? "What's going on with the project?" : 'What was said or observed?'} />
      </div>

      {isInternal ? (
        <div className="mb-4">
          <div style={fieldLabel}>Health flag (optional)</div>
          <div className="flex gap-2 flex-wrap">
            {enumOptions('feedback_health_flag').map(({ value, label }) => {
              const active = healthFlag === value;
              return (
                <button
                  key={value}
                  onClick={() => setHealthFlag(active ? null : value)}
                  style={{
                    padding: '6px 12px', fontSize: 12.5, fontWeight: 500,
                    border: '1px solid', borderRadius: 6, cursor: 'pointer',
                    borderColor: active ? FLAG_COLOR[value] : '#E0DFD9',
                    background: '#fff', color: FLAG_COLOR[value],
                    opacity: active ? 1 : 0.7
                  }}
                >● {label}</button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className={`grid ${sentiment === 'concern' ? 'grid-cols-2' : 'grid-cols-1'} gap-3 mb-4`}>
          <div>
            <div style={fieldLabel}>Channel</div>
            <select style={inputStyle} value={channel} onChange={(e) => setChannel(e.target.value)}>
              {enumOptions('feedback_channel').map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          {sentiment === 'concern' && (
            <div>
              <div style={fieldLabel}>Severity</div>
              <select style={inputStyle} value={severity} onChange={(e) => setSeverity(e.target.value)}>
                {enumOptions('feedback_severity').map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {isEdit && !isInternal && (
        <>
          <div className="mb-4">
            <div style={fieldLabel}>Status</div>
            <select style={inputStyle} value={status} onChange={(e) => setStatus(e.target.value)}>
              {enumOptions('feedback_status').map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <div style={fieldLabel}>Resolution (optional)</div>
            <textarea style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }} value={resolution} onChange={(e) => setResolution(e.target.value)} placeholder="What closed this out?" />
          </div>
        </>
      )}

      {error && (
        <div style={{ marginTop: 8, color: '#9B1F26', fontSize: 12 }}>
          {error}
        </div>
      )}
    </Modal>
  );
}
